import React, { useState, useEffect } from 'react';
import TouchButton from '../ui/TouchButton';
import searchService from '../../services/SearchService';

const SearchResults = ({ 
  results = [], 
  loading = false, 
  error = null,
  onLocationSelect,
  onLoadMore,
  hasMore = false,
  userLocation = null,
  searchQuery = '',
  totalResults = 0,
  className = ''
}) => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [expandedResults, setExpandedResults] = useState(new Set());

  const toggleExpanded = (resultId) => {
    setExpandedResults(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  if (loading && results.length === 0) {
    return (
      <div className={`search-results ${className}`} style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #f0f0f0',
          borderTop: '3px solid #2196F3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }} />
        <div style={{ color: '#666', fontSize: '16px' }}>
          Searching for "{searchQuery}"...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`search-results ${className}`} style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: '#fff3f3',
        borderRadius: '8px',
        border: '1px solid #ffcdd2'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>😞</div>
        <div style={{ color: '#d32f2f', fontSize: '16px', marginBottom: '8px' }}>
          Search Error
        </div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          {error.message || 'Something went wrong while searching'}
        </div>
      </div>
    );
  }

  if (results.length === 0 && searchQuery) {
    return (
      <div className={`search-results ${className}`} style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
        <div style={{ color: '#333', fontSize: '18px', marginBottom: '8px' }}>
          No results found
        </div>
        <div style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
          Try adjusting your search terms or filters
        </div>
        <SearchSuggestions query={searchQuery} />
      </div>
    );
  }

  return (
    <div className={`search-results ${className}`}>
      {/* Results Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '0 4px'
      }}>
        <div style={{ color: '#666', fontSize: '14px' }}>
          {totalResults > 0 ? (
            <>Found {totalResults.toLocaleString()} location{totalResults !== 1 ? 's' : ''}</>
          ) : (
            `${results.length} result${results.length !== 1 ? 's' : ''}`
          )}
          {searchQuery && <> for "<strong>{searchQuery}</strong>"</>}
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <TouchButton
            onClick={() => setViewMode('list')}
            variant={viewMode === 'list' ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '14px', padding: '6px 12px' }}
          >
            📋
          </TouchButton>
          <TouchButton
            onClick={() => setViewMode('grid')}
            variant={viewMode === 'grid' ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '14px', padding: '6px 12px' }}
          >
            ⚏
          </TouchButton>
        </div>
      </div>

      {/* Results List/Grid */}
      <div style={{
        display: viewMode === 'grid' ? 'grid' : 'flex',
        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(300px, 1fr))' : 'none',
        flexDirection: viewMode === 'list' ? 'column' : 'row',
        gap: '16px'
      }}>
        {results.map((result, index) => (
          <SearchResultCard
            key={result.id || index}
            result={result}
            viewMode={viewMode}
            isExpanded={expandedResults.has(result.id)}
            onToggleExpanded={() => toggleExpanded(result.id)}
            onSelect={() => onLocationSelect && onLocationSelect(result)}
            userLocation={userLocation}
          />
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <TouchButton
            onClick={onLoadMore}
            disabled={loading}
            size="large"
            style={{ minWidth: '200px' }}
          >
            {loading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid currentColor',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px'
                }} />
                Loading...
              </>
            ) : (
              'Load More Results'
            )}
          </TouchButton>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const SearchResultCard = ({ 
  result, 
  viewMode, 
  isExpanded, 
  onToggleExpanded, 
  onSelect, 
  userLocation 
}) => {
  const safetyLevel = result.safetyInfo ? 
    searchService.getSafetyLevel(result.safetyInfo.averageRating) : 
    null;

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    position: 'relative',
    overflow: 'hidden'
  };

  const handleCardClick = (e) => {
    e.stopPropagation();
    onSelect && onSelect();
  };

  return (
    <div 
      style={cardStyle}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        e.target.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.target.style.boxShadow = 'none';
        e.target.style.transform = 'translateY(0)';
      }}
    >
      {/* Photo/Header */}
      <div style={{ marginBottom: '12px' }}>
        {result.photos && result.photos.length > 0 ? (
          <div style={{
            width: '100%',
            height: viewMode === 'grid' ? '160px' : '100px',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#f0f0f0'
          }}>
            <img
              src={result.photos[0].thumbnailUrl || result.photos[0].url}
              alt={result.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjBGMEYwIi8+CjxwYXRoIGQ9Ik0zNSA0MEgxNVY2MEgzNVY0MFoiIGZpbGw9IiNEMEQwRDAiLz4KPHA+ath IGQ9Ik00NSA1MEg2NVY3MEg0NVY1MFoiIGZpbGw9IiNEMEQwRDAiLz4KPC9zdmc+';
              }}
            />
          </div>
        ) : (
          <div style={{
            width: '100%',
            height: viewMode === 'grid' ? '160px' : '100px',
            borderRadius: '8px',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px'
          }}>
            {getLocationIcon(result.type)}
          </div>
        )}

        {/* Verification Badge */}
        {result.isVerified && (
          <div style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            ✓ Verified
          </div>
        )}
      </div>

      {/* Content */}
      <div>
        {/* Title and Type */}
        <div style={{ marginBottom: '8px' }}>
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '600',
            color: '#333',
            lineHeight: '1.4',
            marginBottom: '4px'
          }}>
            {result.name}
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            color: '#666'
          }}>
            <span>{getLocationIcon(result.type)} {getLocationTypeLabel(result.type)}</span>
            {result.category && (
              <>
                <span>•</span>
                <span>{getCategoryLabel(result.category)}</span>
              </>
            )}
          </div>
        </div>

        {/* Address */}
        {result.address && (
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '8px',
            lineHeight: '1.4'
          }}>
            📍 {result.address.formatted || 
                 `${result.address.city}, ${result.address.country}`}
          </div>
        )}

        {/* Safety Info */}
        {safetyLevel && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              backgroundColor: `${safetyLevel.color}15`,
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              color: safetyLevel.color
            }}>
              <span>{safetyLevel.icon}</span>
              <span>Safety: {result.safetyInfo.averageRating.toFixed(1)}/5</span>
            </div>
            
            {result.safetyInfo.totalReviews > 0 && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                ({result.safetyInfo.totalReviews} review{result.safetyInfo.totalReviews !== 1 ? 's' : ''})
              </span>
            )}
          </div>
        )}

        {/* Distance */}
        {result.formattedDistance && (
          <div style={{
            fontSize: '12px',
            color: '#666',
            marginBottom: '8px'
          }}>
            📏 {result.formattedDistance} away
          </div>
        )}

        {/* Expanded Details */}
        {isExpanded && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            fontSize: '12px'
          }}>
            {result.safetyInfo && (
              <div style={{ marginBottom: '8px' }}>
                <strong>Safety Breakdown:</strong>
                <div style={{ marginTop: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                  <div>Lighting: ⭐ {result.safetyInfo.ratingBreakdown?.lightingQuality?.toFixed(1) || 'N/A'}</div>
                  <div>Crowd: 👥 {result.safetyInfo.ratingBreakdown?.crowdLevels?.toFixed(1) || 'N/A'}</div>
                  <div>Staff: 🏪 {result.safetyInfo.ratingBreakdown?.staffHelpfulness?.toFixed(1) || 'N/A'}</div>
                  <div>Overall: 🛡️ {result.safetyInfo.ratingBreakdown?.overallSafety?.toFixed(1) || 'N/A'}</div>
                </div>
              </div>
            )}
            
            {result.businessInfo?.hours && (
              <div>
                <strong>Hours:</strong> {getBusinessHours(result.businessInfo.hours)}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '12px'
        }}>
          <TouchButton
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            variant="ghost"
            size="small"
            style={{ fontSize: '12px' }}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </TouchButton>
          
          <TouchButton
            onClick={handleCardClick}
            size="small"
            style={{ fontSize: '12px' }}
          >
            View Details →
          </TouchButton>
        </div>
      </div>
    </div>
  );
};

const SearchSuggestions = ({ query }) => {
  const [popularSearches, setPopularSearches] = useState([]);

  useEffect(() => {
    searchService.getPopularSearches().then(result => {
      setPopularSearches(result.popular?.slice(0, 5) || []);
    });
  }, []);

  return (
    <div>
      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#666' }}>
        Try these popular searches:
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
        {popularSearches.map((search, index) => (
          <TouchButton
            key={index}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
            onClick={() => window.location.search = `?q=${encodeURIComponent(search.term)}`}
          >
            {search.term}
          </TouchButton>
        ))}
      </div>
    </div>
  );
};

// Utility functions
function getLocationIcon(type) {
  const icons = {
    'restaurant': '🍽️', 'hotel': '🏨', 'attraction': '🗼', 'transport': '🚇',
    'shopping': '🛍️', 'nightlife': '🌙', 'park': '🌳', 'beach': '🏖️',
    'museum': '🏛️', 'hospital': '🏥', 'police': '🚔', 'embassy': '🏛️',
    'bank': '🏦', 'pharmacy': '💊', 'gas-station': '⛽', 'airport': '✈️',
    'bus-station': '🚌', 'train-station': '🚂', 'subway': '🚇',
    'university': '🎓', 'school': '🏫', 'library': '📚', 'theater': '🎭',
    'stadium': '🏟️'
  };
  return icons[type] || '📍';
}

function getLocationTypeLabel(type) {
  const labels = {
    'restaurant': 'Restaurant', 'hotel': 'Hotel', 'attraction': 'Attraction',
    'transport': 'Transportation', 'shopping': 'Shopping', 'nightlife': 'Nightlife',
    'park': 'Park', 'beach': 'Beach', 'museum': 'Museum', 'hospital': 'Hospital',
    'police': 'Police', 'embassy': 'Embassy', 'bank': 'Bank', 'pharmacy': 'Pharmacy',
    'gas-station': 'Gas Station', 'airport': 'Airport', 'bus-station': 'Bus Station',
    'train-station': 'Train Station', 'subway': 'Subway', 'university': 'University',
    'school': 'School', 'library': 'Library', 'theater': 'Theater', 'stadium': 'Stadium'
  };
  return labels[type] || 'Location';
}

function getCategoryLabel(category) {
  const labels = {
    'dining': 'Dining', 'accommodation': 'Accommodation', 'entertainment': 'Entertainment',
    'transportation': 'Transportation', 'shopping': 'Shopping', 'health': 'Health',
    'education': 'Education', 'government': 'Government', 'finance': 'Finance',
    'recreation': 'Recreation', 'culture': 'Culture', 'emergency': 'Emergency'
  };
  return labels[category] || 'Other';
}

function getBusinessHours(hours) {
  const today = new Date().toLocaleDateString('en', { weekday: 'long' }).toLowerCase();
  const todayHours = hours[today];
  if (!todayHours || !todayHours.open || !todayHours.close) {
    return 'Hours not available';
  }
  return `Today: ${todayHours.open} - ${todayHours.close}`;
}

export default SearchResults;